import React, { memo, useCallback } from "react";
import { Search, X } from "lucide-react";

interface ChannelManagerFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  status: string;
  setStatus: (status: string) => void;
}

export const ChannelManagerFilters: React.FC<ChannelManagerFiltersProps> = memo(
  ({ searchQuery, setSearchQuery, status, setStatus }) => {
    const handleClear = useCallback(() => setSearchQuery(""), [setSearchQuery]);
    const handleAll = useCallback(() => setStatus("all"), [setStatus]);
    const handleActive = useCallback(() => setStatus("active"), [setStatus]);
    const handleInactive = useCallback(
      () => setStatus("inactive"),
      [setStatus],
    );

    return (
      <div className="bg-white dark:bg-[#111c44] p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
        {/* Search Input */}
        <div className="relative flex-1 group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder="Search by Channel Name or Code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 h-11 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/10 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 font-medium text-slate-700 dark:text-slate-200"
          />
          {searchQuery && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-slate-200 dark:hover:bg-[#111c44] rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Filters Row */}
        <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
          <div className="flex items-center gap-1.5 bg-slate-50 dark:bg-[#111c44] p-1.5 rounded-xl border border-slate-200 dark:border-white/10">
            <button
              onClick={handleAll}
              className={`px-4 h-8 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all ${
                status === "all"
                  ? "bg-white dark:bg-[#1b254b] text-slate-800 dark:text-slate-100 shadow-sm border border-slate-200 dark:border-white/10"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100/50 dark:hover:bg-[#1b254b]/50"
              }`}
            >
              All
            </button>
            <button
              onClick={handleActive}
              className={`px-4 h-8 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all ${
                status === "active"
                  ? "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400 shadow-sm border border-emerald-200 dark:border-emerald-800"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100/50 dark:hover:bg-[#1b254b]/50"
              }`}
            >
              Active
            </button>
            <button
              onClick={handleInactive}
              className={`px-4 h-8 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all ${
                status === "inactive"
                  ? "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-400 shadow-sm border border-rose-200 dark:border-rose-800"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100/50 dark:hover:bg-[#1b254b]/50"
              }`}
            >
              Inactive
            </button>
          </div>
        </div>
      </div>
    );
  },
);
ChannelManagerFilters.displayName = "ChannelManagerFilters";
